import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Header from '../../components/ui/Header';
import WorkflowProgress from '../../components/ui/WorkflowProgress';
import SyncStatusNotifications from '../../components/ui/SyncStatusNotifications';
import SyncSummaryCard from './components/SyncSummaryCard';
import ResultsTabNavigation from './components/ResultsTabNavigation';
import SongResultCard from './components/SongResultCard';
import PlaylistManagementActions from './components/PlaylistManagementActions';
import ExportOptions from './components/ExportOptions';
import ManualMatchModal from './components/ManualMatchModal';

const SyncResultsManagement = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('successful');
  const [showExportOptions, setShowExportOptions] = useState(false);
  const [selectedSong, setSelectedSong] = useState(null);
  const [showManualMatch, setShowManualMatch] = useState(false);

  // Mock sync result data
  const syncResult = {
    id: 'sync_001',
    status: 'completed',
    playlistName: 'My Favorite Rock Hits',
    sourcePlatform: 'Spotify',
    destinationPlatform: 'YouTube Music',
    totalTracks: 45,
    successCount: 38,
    failedCount: 5,
    duplicatesCount: 2,
    manualReviewCount: 3,
    duration: 142, // seconds
    startTime: new Date(Date.now() - 142000),
    endTime: new Date(),
    canRollback: true
  };

  // Mock songs data
  const songsData = {
    successful: [
      {
        id: 'song1',
        title: 'Bohemian Rhapsody',
        artist: 'Queen',
        album: 'A Night at the Opera',
        duration: 355,
        albumArt: 'https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?w=100&h=100&fit=crop',
        matchConfidence: 98,
        sourcePlatform: 'Spotify',
        targetPlatform: 'YouTube Music',
        syncTime: new Date(Date.now() - 120000),
        matchedSong: {
          title: 'Bohemian Rhapsody',
          artist: 'Queen'
        }
      },
      {
        id: 'song2',
        title: 'Stairway to Heaven',
        artist: 'Led Zeppelin',
        album: 'Led Zeppelin IV',
        duration: 482,
        albumArt: 'https://images.unsplash.com/photo-1514320291840-2e0a9bf2a9ae?w=100&h=100&fit=crop',
        matchConfidence: 95,
        sourcePlatform: 'Spotify',
        targetPlatform: 'YouTube Music',
        syncTime: new Date(Date.now() - 110000),
        matchedSong: {
          title: 'Stairway to Heaven',
          artist: 'Led Zeppelin'
        }
      }
    ],
    failed: [
      {
        id: 'song3',
        title: 'Rare B-Side Track',
        artist: 'Indie Band',
        album: 'Limited Edition EP',
        duration: 245,
        albumArt: 'https://images.unsplash.com/photo-1511379938547-c1f69419868d?w=100&h=100&fit=crop',
        sourcePlatform: 'Spotify',
        targetPlatform: 'YouTube Music',
        syncTime: new Date(Date.now() - 100000),
        failureReason: 'Song not available on YouTube Music'
      },
      {
        id: 'song4',
        title: 'Live Concert Recording',
        artist: 'Rock Band',
        album: 'Live Album',
        duration: 320,
        albumArt: 'https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?w=100&h=100&fit=crop',
        sourcePlatform: 'Spotify',
        targetPlatform: 'YouTube Music',
        syncTime: new Date(Date.now() - 90000),
        failureReason: 'Multiple versions found, manual selection required'
      }
    ],
    duplicates: [
      {
        id: 'song5',
        title: 'Hotel California',
        artist: 'Eagles',
        album: 'Hotel California',
        duration: 391,
        albumArt: 'https://images.unsplash.com/photo-1514320291840-2e0a9bf2a9ae?w=100&h=100&fit=crop',
        sourcePlatform: 'Spotify',
        targetPlatform: 'YouTube Music',
        syncTime: new Date(Date.now() - 80000),
        failureReason: 'Song already exists in destination playlist'
      }
    ],
    manual: [
      {
        id: 'song6',
        title: 'Ambiguous Song Title',
        artist: 'Common Artist Name',
        album: 'Generic Album',
        duration: 210,
        albumArt: 'https://images.unsplash.com/photo-1511379938547-c1f69419868d?w=100&h=100&fit=crop',
        sourcePlatform: 'Spotify',
        targetPlatform: 'YouTube Music',
        syncTime: new Date(Date.now() - 70000),
        failureReason: 'Low confidence match, manual review recommended'
      }
    ]
  };

  const handleRetry = (songId) => {
    console.log('Retrying sync for song:', songId);
    // Implement retry logic
  };

  const handleManualMatch = (songId) => {
    const song = Object.values(songsData).flat().find(s => s.id === songId);
    setSelectedSong(song);
    setShowManualMatch(true);
  };

  const handleConfirmMatch = (songId, matchedSong) => {
    console.log('Confirming match for song:', songId, 'with:', matchedSong);
    // Implement match confirmation logic
    setShowManualMatch(false);
    setSelectedSong(null);
  };

  const handlePlaylistAction = (action, data) => {
    switch (action) {
      case 'viewPlaylist':
        window.open('https://music.youtube.com/playlist?list=example', '_blank');
        break;
      case 'syncAgain': navigate('/playlist-selection-sync-setup');
        break;
      case 'reverseSync': navigate('/playlist-selection-sync-setup', { 
          state: { reverseSync: true, sourcePlaylist: syncResult } 
        });
        break;
      case 'schedule': console.log('Scheduling sync with frequency:', data.frequency);
        break;
      case 'exportLogs':
        setShowExportOptions(true);
        break;
      case 'rollback': console.log('Rolling back sync');
        break;
      default:
        console.log('Unknown action:', action);
    }
  };

  const handleExport = async (optionId, format) => {
    console.log('Exporting:', optionId, 'in format:', format);
    // Simulate export process
    await new Promise(resolve => setTimeout(resolve, 2000));
    
    // Create and download mock file
    const mockData = `Sync Results Export\nPlaylist: ${syncResult.playlistName}\nTotal: ${syncResult.totalTracks}\nSuccessful: ${syncResult.successCount}\nFailed: ${syncResult.failedCount}`;
    const blob = new Blob([mockData], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `sync_results_${Date.now()}.${format}`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const getCurrentSongs = () => {
    return songsData[activeTab] || [];
  };

  const getCounts = () => ({
    successful: songsData.successful.length,
    failed: songsData.failed.length,
    duplicates: songsData.duplicates.length,
    manual: songsData.manual.length
  });

  return (
    <div className="min-h-screen bg-background">
      {/* <Header /> */}
      <WorkflowProgress />
      {/* <SyncStatusNotifications /> */}

      <main className="pt-32 lg:pt-36 pb-8">
        <div className="max-w-7xl mx-auto px-4 lg:px-6">
          {/* Breadcrumb */}
          <nav className="flex items-center space-x-2 text-sm text-text-muted mb-6">
            <button 
              onClick={() => navigate('/dashboard-sync-overview')}
              className="hover:text-text-primary transition-colors duration-200"
            >
              Dashboard
            </button>
            <Icon name="ChevronRight" size={16} />
            <button 
              onClick={() => navigate('/sync-progress-real-time-tracking')}
              className="hover:text-text-primary transition-colors duration-200"
            >
              Sync Progress
            </button>
            <Icon name="ChevronRight" size={16} />
            <span className="text-text-primary">Results</span>
          </nav>

          {/* Page Header */}
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-8">
            <div>
              <h1 className="text-2xl lg:text-3xl font-bold text-text-primary mb-2">
                Sync Results & Management
              </h1>
              <p className="text-text-secondary">
                Review sync outcomes and manage your synchronized playlists
              </p>
            </div>
            <div className="flex items-center space-x-3 mt-4 lg:mt-0">
              <Button
                variant="outline"
                iconName="Download"
                iconSize={16}
                onClick={() => setShowExportOptions(!showExportOptions)}
              >
                Export
              </Button>
              <Button
                variant="primary"
                iconName="RotateCcw"
                iconSize={16}
                onClick={() => navigate('/playlist-selection-sync-setup')}
              >
                New Sync
              </Button>
            </div>
          </div>

          {/* Sync Summary */}
          <div className="mb-8">
            <SyncSummaryCard syncResult={syncResult} />
          </div>

          {/* Results Tabs and Content */}
          <div className="bg-surface border border-border rounded-lg shadow-elevation-1 mb-8">
            <ResultsTabNavigation
              activeTab={activeTab}
              onTabChange={setActiveTab}
              counts={getCounts()}
            />

            <div className="p-6">
              {getCurrentSongs().length > 0 ? (
                <div className="space-y-4">
                  {getCurrentSongs().map((song) => (
                    <SongResultCard
                      key={song.id}
                      song={song}
                      type={activeTab}
                      onRetry={handleRetry}
                      onManualMatch={handleManualMatch}
                    />
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <Icon name="Music" size={48} className="text-text-muted mx-auto mb-4" />
                  <p className="text-text-secondary">No songs in this category</p>
                </div>
              )}
            </div>
          </div>

          {/* Playlist Management Actions */}
          <div className="bg-surface border border-border rounded-lg shadow-elevation-1 p-6 mb-8">
            <h2 className="text-lg font-semibold text-text-primary mb-4">
              Playlist Management
            </h2>
            <PlaylistManagementActions
              syncResult={syncResult}
              onAction={handlePlaylistAction}
            />
          </div>

          {/* Export Options */}
          {showExportOptions && (
            <div className="bg-surface border border-border rounded-lg shadow-elevation-1 p-6">
              <ExportOptions
                syncResult={syncResult}
                onExport={handleExport}
              />
            </div>
          )}
        </div>
      </main>

      {/* Manual Match Modal */}
      <ManualMatchModal
        isOpen={showManualMatch}
        onClose={() => {
          setShowManualMatch(false);
          setSelectedSong(null);
        }}
        song={selectedSong}
        onMatch={handleConfirmMatch}
      />
    </div>
  );
};

export default SyncResultsManagement;